import { $http } from '~/boot/plugins/interceptors'

export type PasswordChange = {
  old_password: string
  password: string
  password_confirmation: string
}

export type IdleTimeoutSettings = {
  idle_timeout: number
  effective_timeout?: number
}

export type OrganizationIdleTimeoutSettings = {
  enforce_max_timeout: boolean
  default_timeout?: number
  max_timeout_limit?: number
}

export type FlowPreferences = {
  focus_preference: 'morning' | 'afternoon' | 'no_preference'
  location_anchor: 'home' | 'nearest'
  max_daily_gap: number
  hard_rules?: {
    locations?: number[]
    excluded_activities?: number[]
    travel_buffer_minutes?: number
  }
}

export type EmailChangeRequest = {
  new_email: string
  current_password: string
}

export type PendingEmailChange = {
  pending_email: string | null
  requested_at: string | null
}

export type FeatureFlags = {
  chat_enabled: boolean
}

export default {
  async medicalTerms(): Promise<string[]> {
    const { data } = await $http.get<{ data: string }>(`/settings/medical-terms`)
    return data.data.split(/\r?\n/)
  },

  async updateLocale(locale: string): Promise<void> {
    return await $http.put(`/settings/locale`, { locale })
  },

  async changePassword(payload: PasswordChange): Promise<void> {
    return await $http.put(`/settings/change-password`, payload)
  },

  async getUserIdleTimeout(): Promise<IdleTimeoutSettings> {
    const { data } = await $http.get<IdleTimeoutSettings>(`/user/idle-timeout`)
    return data
  },

  async updateUserIdleTimeout(payload: IdleTimeoutSettings): Promise<IdleTimeoutSettings> {
    const { data } = await $http.post<IdleTimeoutSettings>(`/user/idle-timeout`, payload)
    return data
  },

  async getOrganizationIdleTimeoutSettings(): Promise<OrganizationIdleTimeoutSettings> {
    const { data } = await $http.get<OrganizationIdleTimeoutSettings>(
      `/organisation/idle-timeout/settings`,
    )
    return data
  },

  async getFlowPreferences(): Promise<FlowPreferences> {
    const { data } = await $http.get<FlowPreferences>(`/settings/flow-preferences`)
    return data
  },

  async updateFlowPreferences(payload: FlowPreferences): Promise<void> {
    return await $http.put(`/settings/flow-preferences`, payload)
  },

  async requestEmailChange(payload: EmailChangeRequest): Promise<{ message: string }> {
    const { data } = await $http.post<{ message: string }>(
      `/settings/change-email/request`,
      payload,
    )
    return data
  },

  async getPendingEmailChange(): Promise<PendingEmailChange> {
    const { data } = await $http.get<PendingEmailChange>(`/settings/change-email/pending`)
    return data
  },

  async cancelEmailChange(): Promise<{ message: string }> {
    const { data } = await $http.delete<{ message: string }>(`/settings/change-email/cancel`)
    return data
  },

  async verifyEmailChangeOtp(otp: string): Promise<{ message: string }> {
    const { data } = await $http.post<{ message: string }>(`/settings/change-email/verify-otp`, {
      otp,
    })
    return data
  },

  async resendEmailChangeOtp(): Promise<{ message: string }> {
    const { data } = await $http.post<{ message: string }>(`/settings/change-email/resend-otp`)
    return data
  },

  async featureFlags(): Promise<FeatureFlags> {
    const { data } = await $http.get<FeatureFlags>(`/settings/features`)
    return data
  },
}
